/**
 * Engagement store — XP, streak, mastery, badges.
 *
 * Local-first MVP: persisted to localStorage so it works for logged-out
 * visitors. Once a user account is wired, the same shape can sync to the
 * backend.
 *
 * XP economy (tuneable):
 *   - Open a teaching block:     +5 XP
 *   - Answer NCLEX (any):        +5 XP
 *   - Answer NCLEX correctly:    +15 XP
 *   - Complete a shift case:     +25 XP
 *   - Daily login:               +10 XP (resets streak on miss)
 *
 * Levels: each level requires (level * 100) XP to advance.
 * Mastery: per-topic 0-100, hand-curated topics for now.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const KEY = "maldek_engagement_v1";

const DEFAULT = {
  xp: 0,
  level: 1,
  streakDays: 0,
  lastVisitISO: null,
  badges: [],
  mastery: {},     // { "lactate": 0-100, "hyperkalemia": 0-100, ... }
  events: 0,       // total recorded events (rough usage signal)
};

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT;
    return { ...DEFAULT, ...JSON.parse(raw) };
  } catch { return DEFAULT; }
}

function save(state) {
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

function xpForLevel(level) { return level * 100; }

function applyXP(state, amount) {
  let xp = state.xp + amount;
  let level = state.level;
  while (xp >= xpForLevel(level)) {
    xp -= xpForLevel(level);
    level += 1;
  }
  return { ...state, xp, level, events: state.events + 1 };
}

function ymd(d) { return d.toISOString().slice(0, 10); }

function applyVisit(state) {
  const today = new Date();
  const todayY = ymd(today);
  if (state.lastVisitISO === todayY) return state;       // same day, no change
  const yesterday = new Date(today.getTime() - 24 * 3600 * 1000);
  const wasYesterday = state.lastVisitISO === ymd(yesterday);
  const newStreak = wasYesterday ? state.streakDays + 1 : 1;
  return {
    ...state,
    streakDays: newStreak,
    lastVisitISO: todayY,
    xp: state.xp + 10,
  };
}

const ALL_BADGES = [
  { id: "first-login",       label: "First visit",    icon: "✦",  rule: (s) => s.events >= 1 },
  { id: "streak-3",          label: "3-day streak",   icon: "🔥", rule: (s) => s.streakDays >= 3 },
  { id: "streak-7",          label: "Week warrior",   icon: "🔥", rule: (s) => s.streakDays >= 7 },
  { id: "streak-30",         label: "30-day spark",   icon: "🔥", rule: (s) => s.streakDays >= 30 },
  { id: "level-2",           label: "Level 2",        icon: "△",  rule: (s) => s.level >= 2 },
  { id: "level-5",           label: "Level 5",        icon: "◇",  rule: (s) => s.level >= 5 },
  { id: "level-10",          label: "Level 10",       icon: "✧",  rule: (s) => s.level >= 10 },
  { id: "nclex-10",          label: "10 NCLEX-corr",  icon: "✓",  rule: (s) => (s.mastery.nclex || 0) >= 10 },
];

function recomputeBadges(state) {
  const earned = new Set(state.badges);
  ALL_BADGES.forEach((b) => { if (b.rule(state)) earned.add(b.id); });
  return { ...state, badges: Array.from(earned) };
}

const EngagementCtx = createContext({
  state: DEFAULT,
  awardXP: () => {},
  awardCorrect: () => {},
  awardOpen: () => {},
  bumpMastery: () => {},
  badges: ALL_BADGES,
});

export function EngagementProvider({ children }) {
  const [state, setState] = useState(load);

  // Daily-visit XP + streak update — runs once per app load.
  useEffect(() => {
    const next = recomputeBadges(applyVisit(state));
    if (next !== state) { setState(next); save(next); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const awardXP = useCallback((amount = 5) => {
    setState((s) => {
      const next = recomputeBadges(applyXP(s, amount));
      save(next); return next;
    });
  }, []);

  const awardOpen = useCallback(() => awardXP(5), [awardXP]);

  const awardCorrect = useCallback((topic) => {
    setState((s) => {
      let next = applyXP(s, 15);
      if (topic) {
        next = {
          ...next,
          mastery: {
            ...next.mastery,
            [topic]: Math.min(100, (next.mastery[topic] || 0) + 10),
            nclex: (next.mastery.nclex || 0) + 1,
          },
        };
      }
      next = recomputeBadges(next);
      save(next); return next;
    });
  }, []);

  const bumpMastery = useCallback((topic, delta = 5) => {
    setState((s) => {
      const next = recomputeBadges({
        ...s,
        mastery: { ...s.mastery, [topic]: Math.min(100, (s.mastery[topic] || 0) + delta) },
      });
      save(next); return next;
    });
  }, []);

  return (
    <EngagementCtx.Provider value={{ state, awardXP, awardCorrect, awardOpen, bumpMastery, badges: ALL_BADGES }}>
      {children}
    </EngagementCtx.Provider>
  );
}

export const useEngagement = () => useContext(EngagementCtx);
export const xpToNextLevel = (state) => xpForLevel(state.level) - state.xp;
export const xpProgressPct = (state) =>
  Math.round((state.xp / xpForLevel(state.level)) * 100);
